/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { Button, Checkbox, Row, Col, Popover, Dropdown, Switch } from 'antd';
import {
  FilePdfOutlined,
  FileExcelOutlined,
  FilterOutlined,
  ReloadOutlined,
  FilterFilled,
  DownOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from '@ant-design/icons';

import { NoteModal } from './../note-modal';

import * as actions from '../store/actions';

import NotesFilter from '../notes-filter';

interface NotesHeaderProps {
  showArchiveChange: (checked: boolean) => void;
  showArchiveEnabled: boolean;
  isMomentumEnabled: boolean;
  compactModeChange: (checked: boolean) => void;
  compactModeEnabled: boolean;
  sortChange: (direction: string) => void;
  refresh: () => void;
  reloadData: () => void;
  fetchFilteredData: (filters: any) => void;
  exportNotesToPdf: () => void;
  exportNotesToExcel: () => void;
  baseRequestParams: any;
  isHistoryNotes: boolean | undefined;
  isSuperUserNotes: boolean | undefined;
  initialFilterValues: any;
}

interface MenuItem {
  key: string;
  icon: React.ReactNode;
  onClick: () => void;
  label: string;
}

const NotesHeader: React.FC<NotesHeaderProps> = ({
  showArchiveChange,
  showArchiveEnabled,
  isMomentumEnabled,
  compactModeChange,
  compactModeEnabled,
  sortChange,
  refresh,
  reloadData,
  fetchFilteredData,
  exportNotesToPdf,
  exportNotesToExcel,
  baseRequestParams,
  isHistoryNotes,
  isSuperUserNotes,
  initialFilterValues,
}) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [showArchive, setShowArchive] = useState<boolean>(showArchiveEnabled);
  const [compactMode, setCompactMode] = useState<boolean>(compactModeEnabled);
  const [filterPanelVisible, setFilterPanelVisible] = useState<boolean>(false);
  const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);
  const [createDateSortDirection, setCreateDateSortDirection] = useState<string>('desc');

  const dispatch = useDispatch();

  useEffect(() => {
    if (initialFilterValues) {
      let isFilterApplied = false;

      for (const [, value] of Object.entries(initialFilterValues)) {
        if (value !== undefined && value !== null && (!Array.isArray(value) || (Array.isArray(value) && value.length > 0))) {
          isFilterApplied = true;
          break;
        }
      }

      setIsFilterApplied(isFilterApplied);
    }
  }, [initialFilterValues]);

  useEffect(() => {
    setShowArchive(showArchiveEnabled);
    setCompactMode(compactModeEnabled);
  }, [showArchiveEnabled, compactModeEnabled]);

  const handleChangeCompactMode = (checked: boolean): void => {
    setCompactMode(checked);
    compactModeChange(checked);
  };

  const handleChangeShowArchive = (checked: boolean): void => {
    setShowArchive(checked);
    showArchiveChange(checked);
  };

  const handleModalVisibilityChange = (visible: boolean): void => {
    dispatch(actions.addNoteModalStateChange(visible) as any);
    setTimeout(() => setVisible(visible));
  };

  const filterAppliedChanging = (value: boolean): void => {
    setIsFilterApplied(value);
  };

  const filterPanelVisibleChanging = (value: boolean): void => {
    setFilterPanelVisible(value);
  };

  const handleSortNotes = (): void => {
    let direction = createDateSortDirection;

    if (direction === 'asc') {
      direction = 'desc';
    } else {
      direction = 'asc';
    }

    setCreateDateSortDirection(direction);

    sortChange(direction);
  };

  const menu: MenuItem[] = [
    {
      key: '1',
      icon: <FilePdfOutlined />,
      onClick: exportNotesToPdf,
      label: 'PDF',
    },
    {
      key: '2',
      icon: <FileExcelOutlined />,
      onClick: exportNotesToExcel,
      label: 'Excel',
    },
  ];

  return (
    <>
      <Row>
        <Col span={9} className="note-header-title">
          <span style={{ marginRight: '20px', fontWeight: 600 }}>{isSuperUserNotes ? 'Super User Notes' : 'Notes'}</span>
          {isHistoryNotes ? null : (
            <Button ghost type="primary" size="small" style={{ marginRight: '10px' }} onClick={() => handleModalVisibilityChange(true)}>
              Add new
            </Button>
          )}
        </Col>

        <Col span={15} className="note-header-actions">
          <Button
            type="link"
            title={'Sort notes: ' + (createDateSortDirection === 'asc' ? 'New to Old' : 'Old to New')}
            onClick={() => handleSortNotes()}
          >
            Create Date
            {createDateSortDirection === 'asc' ? (
              <SortAscendingOutlined style={{ marginLeft: '4px' }} />
            ) : (
              <SortDescendingOutlined style={{ marginLeft: '4px' }} />
            )}
          </Button>

          <Switch
            size="small"
            checked={compactMode}
            onChange={(checked) => handleChangeCompactMode(checked)}
            title="Switch between normal and compact control view"
            checkedChildren="Compact"
            unCheckedChildren="Normal"
          ></Switch>

          <Checkbox className="nc-notes-checkbox" checked={showArchive} onChange={(e) => handleChangeShowArchive(e.target.checked)}>
            <span style={{ fontWeight: 600 }}>Show Archived</span>
          </Checkbox>

          <Dropdown menu={{ items: menu }} trigger={['click']}>
            <a className="ant-dropdown-link" onClick={(e) => e.preventDefault()} style={{ marginRight: '20px' }}>
              Export as <DownOutlined />
            </a>
          </Dropdown>

          <Button type="primary" title="Refresh" ghost icon={<ReloadOutlined />} onClick={() => reloadData()} />

          <Popover
            overlayStyle={{ width: '680px' }}
            content={
              <NotesFilter
                fetchFilteredData={fetchFilteredData}
                filterAppliedChanging={filterAppliedChanging}
                filterPanelVisibleChanging={filterPanelVisibleChanging}
                isHistoryNotes={isHistoryNotes}
                initialFilterValues={initialFilterValues}
              />
            }
            title="Filter"
            trigger="click"
            open={filterPanelVisible}
            placement="bottomRight"
          >
            <Button
              type="primary"
              ghost
              icon={isFilterApplied ? <FilterFilled /> : <FilterOutlined />}
              title="Filter"
              onClick={() => filterPanelVisibleChanging(!filterPanelVisible)}
            />
          </Popover>
        </Col>
      </Row>

      {/* DO NOT WRAP IN TERNARY EXPRESSION */}
      <NoteModal
        visible={visible}
        close={() => handleModalVisibilityChange(false)}
        refresh={() => refresh()}
        baseRequestParams={baseRequestParams}
        isSuperUserNotes={isSuperUserNotes}
        isMomentumEnabled={isMomentumEnabled}
      />
    </>
  );
};

export default NotesHeader;
